import { useState } from "react";

import { Plus } from "lucide-react";
import { Link } from "react-router";

import { isProgramLead } from "@/components/helpers/workspace/permissions";
import { AddProgramComponentDialog } from "@/components/pages_logged_in/program/components/AddProgramComponentDialog";
import { Button } from "@/components/ui/button";
import { ProgramStatus } from "@/graphql";
import { MainProgram } from "@/types/programs";

type Action = "addComponent" | "addParticipants";

interface QuickActionsProps {
  actions: Action[];
  program: MainProgram;
  isLead: boolean;
}

export function QuickActions({
  actions = [],
  program,
  isLead = false,
}: QuickActionsProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!program) return null;

  const activeProgram = [
    ProgramStatus.LaunchReady,
    ProgramStatus.Live,
    ProgramStatus.InDesign,
  ].includes(program.status);

  if (actions.length === 0 || !isLead || !activeProgram) {
    return null;
  }

  const programLeads = program.programOwners;
  const hasProgramLeads = programLeads.length > 0;
  const canAddComponent =
    isProgramLead(program.id) && hasProgramLeads && activeProgram;

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  return (
    <div>
      <div className="text-base font-semibold">Quick Actions</div>
      <div className="max-w-[275px]">
        {actions.includes("addComponent") && canAddComponent && (
          <div className="mt-3">
            <Button
              intent="secondary"
              variant="outline"
              onClick={openDialog}
              className="w-full"
            >
              <Plus className="size-4" />
              Add Component
            </Button>
            <AddProgramComponentDialog
              programId={program.id}
              open={isDialogOpen}
              onClose={closeDialog}
              redirect=""
              redirectByComponentType={false}
            />
          </div>
        )}
        {actions.includes("addParticipants") && (
          <div className="mt-3">
            <Button
              asChild
              intent="secondary"
              variant="outline"
              className="w-full"
            >
              <Link to={`/programs/${program.id}/participants`}>
                <Plus className="size-4" />
                Add Participants
              </Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
